import { useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/internationalization/cn'
import { useAnchoredPosition } from '@/hooks'

export interface TooltipProps {
  /** `null` shows no bubble, so a trigger can keep one stable tree. */
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
}

/**
 * Supplementary hint only. Never put an instruction a user must read in a
 * tooltip — it does not exist on touch devices, where there is no hover.
 * Anything essential goes in a hint line under the field.
 *
 * Opens on focus as well as hover, so keyboard users get it too.
 *
 * PORTALED AND VIEWPORT-POSITIONED. It used to place itself with `absolute`
 * inside the trigger, which any overflow ancestor clips — and `Card` is
 * `overflow-hidden`, so a tooltip on the first row of any card was sliced in
 * half by the card's own edge. `position: fixed` in a portal escapes every such
 * ancestor, and the anchor hook flips the tooltip below the trigger when there
 * is no room above.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)

  const position = useAnchoredPosition(triggerRef, tipRef, {
    isOpen: isVisible,
    side,
    align: 'center',
  })

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-flex"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </span>
      {isVisible &&
        content != null &&
        createPortal(
          <span
            ref={tipRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
            className={cn(
              'pointer-events-none z-popover w-max max-w-64 animate-fade-in rounded',
              'bg-fg px-2 py-1 text-xs font-medium text-fg-inverted shadow-md',
              className,
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </>
  )
}
